import { useMemo } from "react";
import _set from "lodash.set";
import _get from "lodash.get";
import type { Style, AnyLayer, AnySourceImpl } from "mapbox-gl";

import useMapStateStore from "./use-map-style-store";

const useMapStyle = () => {
  const baseMapStyle = useMapStateStore((store) => store.baseMapStyle);
  const customisations = useMapStateStore((store) => store.customisations);

  const mapStyle = useMemo(() => {
    // Can only perform on a loaded map style
    if (baseMapStyle == null) {
      return null;
    }

    // Perform the customisations on the style
    if (customisations.length === 0) {
      return baseMapStyle;
    }

    // Prioritise
    const prioritisedStyleCustomisations = Array.from(customisations);
    prioritisedStyleCustomisations.sort((a, b) => b.priority - a.priority);

    // Duplicate our base style to customise
    const nextMapStyle = prioritisedStyleCustomisations.reduce(
      (mapStyle, styleCustomisation) => {
        return styleCustomisation.customisations.reduce(
          (mapStyle, customisation) => {
            switch (customisation.type) {
              case "AddLayer": {
                return layerAdd(
                  mapStyle,
                  customisation.layer,
                  customisation.before
                );
              }

              case "SetLayerValue": {
                return layerSet(
                  mapStyle,
                  customisation.layerId,
                  customisation.path,
                  customisation.value
                );
              }

              case "RemoveLayer": {
                return layerRemove(mapStyle, customisation.layerId);
              }

              case "AddSource": {
                return sourceAdd(
                  mapStyle,
                  customisation.id,
                  customisation.source
                );
              }

              case "SetSourceValue": {
                return sourceSet(
                  mapStyle,
                  customisation.sourceId,
                  customisation.path,
                  customisation.value
                );
              }
            }

            return mapStyle;
          },
          mapStyle
        );
      },
      baseMapStyle
    );

    return nextMapStyle;
  }, [baseMapStyle, customisations]);

  return { mapStyle };
};

export default useMapStyle;

export const layerSet = (
  original: Style,
  id: string | RegExp,
  path: Parameters<typeof _set>[1],
  value: any
) => {
  try {
    return Object.assign({}, original, {
      layers: (original.layers || []).map((l: AnyLayer) =>
        (typeof id === "string" && id === l.id) ||
        (typeof id === "object" && id.test(l.id))
          ? _set(
              JSON.parse(JSON.stringify(l)),
              path,
              typeof value === "function" ? value(_get(l, path)) : value
            )
          : l
      ),
    });
  } catch (e: any) {
    console.warn(e);
  }
  return original;
};

export const sourceSet = (
  original: Style,
  id: string | RegExp,
  path: Parameters<typeof _set>[1],
  value: any
) => {
  try {
    return Object.assign({}, original, {
      sources: Object.keys(original.sources || {}).reduce(
        (c, t) =>
          (typeof id === "string" && id === t) ||
          (typeof id === "object" && id.test(t))
            ? Object.assign({}, c, {
                [t]: _set(
                  JSON.parse(JSON.stringify((original.sources || {})[t])),
                  path,
                  typeof value === "function"
                    ? value(_get(original.sources[t], path))
                    : value
                ),
              })
            : Object.assign({}, c, { [t]: (original.sources || {})[t] }),
        []
      ),
    });
  } catch (e: any) {
    console.warn(e);
  }
  return original;
};

export const layerAdd = (
  original: Style,
  layer: AnyLayer,
  before?: string | string[] | RegExp
): Style => {
  try {
    const layers = (Array.isArray(layer) && layer) || [layer];
    // If we already if the layer in the style layers, remove it first
    const currentLayers = (original.layers || []).filter(
      (l: AnyLayer) => !layers.find((lay: AnyLayer) => lay.id === l.id)
    );
    if (before) {
      // Splice is mutating..
      const newLayers = currentLayers;
      const start = newLayers.findIndex((l: AnyLayer) => isBefore(l, before));
      if (start === -1) {
        return Object.assign({}, original, {
          layers: [...newLayers, layer],
        });
      }
      newLayers.splice(start, 0, ...layers);
      return Object.assign({}, original, {
        layers: newLayers,
      });
    }
    return Object.assign({}, original, {
      layers: currentLayers.concat(...layers),
    });
  } catch (e: any) {
    console.warn(e);
  }
  return original;
};

function isBefore(l: AnyLayer, before: string | string[] | RegExp) {
  if (typeof before === "string") {
    return l.id === before;
  }
  if ("test" in before) {
    return before.test(l.id);
  }
  return before.indexOf(l.id) !== -1;
}

export const layerRemove = (original: Style, id: string | RegExp): Style => {
  try {
    return Object.assign({}, original, {
      layers: (original.layers || []).filter((layer: AnyLayer) => {
        if (typeof id === "string") {
          return layer.id !== id;
        }
        if (id && id.test) {
          return id.test(layer.id);
        }
        return false;
      }),
    });
  } catch (e: any) {
    console.warn(e);
  }
  return original;
};

export const sourceReplace = (
  original: Style,
  id: string,
  source: AnySourceImpl
): Style => {
  try {
    const sources = Object.assign({}, original.sources || {}, { [id]: source });
    return Object.assign({}, original, { sources });
  } catch (e: any) {
    console.warn(e);
  }
  return original;
};

export const sourceAdd = sourceReplace;

export const sourceRemove = (original: Style, id: string): Style => {
  try {
    const originalSources = Object.assign({}, original.sources);
    const sources = Object.keys(originalSources)
      .filter((key) => key !== id)
      .reduce((c, key) => {
        return Object.assign({}, c, { [key]: originalSources[key] });
      }, {});
    return Object.assign({}, original, { sources });
  } catch (e: any) {
    console.warn(e);
  }
  return original;
};
